import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, ExternalLink, Keyboard, X, Play } from '@/components/icons';
import { KEYBOARD_SHORTCUTS, RESOURCE_LINKS } from '@/data/keyboardShortcuts';
import { motion, AnimatePresence } from 'framer-motion';
import { useOnboarding } from '@/context/OnboardingContext';
import { Tooltip } from '@/components/ui/beui';

interface KeyboardCheatSheetProps {
  variant?: 'icon' | 'button';
}

export const KeyboardCheatSheet: React.FC<KeyboardCheatSheetProps> = ({
  variant = 'icon',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { resetOnboarding } = useOnboarding();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRestartTour = () => {
    resetOnboarding();
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {variant === 'icon' ? (
        <Tooltip content="Help & Shortcuts">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="flex items-center justify-center p-3 rounded-full bg-surface-secondary border border-border shadow-lg text-muted-foreground hover:text-foreground hover:bg-surface-tertiary transition-all dark:border-border dark:text-muted-foreground dark:hover:text-foreground"
            aria-label="Help & Shortcuts"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </Tooltip>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span>Help</span>
        </button>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-full right-0 mb-2 w-72 bg-surface-primary border border-border rounded-xl shadow-xl overflow-hidden z-50"
          >
            <div className="flex items-center justify-between px-3 py-2 bg-surface-secondary border-b border-border">
              <div className="flex items-center gap-2">
                <Keyboard className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">Shortcuts</span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-md hover:bg-surface-tertiary transition-colors"
              >
                <X className="w-3 h-3 text-muted-foreground" />
              </button>
            </div>

            <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
              {KEYBOARD_SHORTCUTS.map((category) => (
                <div key={category.category}>
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
                    {category.category}
                  </p>
                  <div className="space-y-1">
                    {category.items.map((item, index) => (
                      <div
                        key={`${item.key}-${index}`}
                        className="flex items-center justify-between py-1"
                      >
                        <span className="text-xs text-foreground">{item.description}</span>
                        <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-surface-secondary rounded border border-border">
                          {item.key}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-2 border-t border-border">
              <button
                onClick={handleRestartTour}
                className="flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-primary dark:text-primary bg-primary/10 dark:bg-primary/20 rounded-md hover:bg-primary/10 dark:hover:bg-primary/30 transition-colors"
              >
                <Play className="w-4 h-4" />
                Restart Tour
              </button>
            </div>

            <div className="p-2 border-t border-border bg-surface-secondary">
              <p className="text-[10px] font-medium text-muted-foreground mb-1.5 px-1">Resources</p>
              <div className="space-y-0.5">
                {RESOURCE_LINKS.map((link) => (
                  <a
                    key={link.url}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-1.5 rounded hover:bg-surface-tertiary transition-colors group"
                    onClick={() => setIsOpen(false)}
                  >
                    <span className="text-xs group-hover:text-primary transition-colors">
                      {link.title}
                    </span>
                    <ExternalLink className="w-3 h-3 text-muted-foreground group-hover:text-primary transition-colors" />
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default KeyboardCheatSheet;
